import { addBreadcrumb } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import type { AccessDenialReason } from '@workspace/shared';

import { isDialogToParentMessage } from '@/api/types/authMessages';
import { readAndClearBootstrapHandoff } from '@/config/auth/bootstrapHandoff';
import {
  LoginDialogLostError,
  LoginDialogTimedOutError,
  loginViaOfficeDialog,
} from '@/config/auth/clerkAuthDialog';
import {
  claimLoginHandoff,
  claimLoginHandoffOnce,
  generateLoginHandoffId,
  type LoginHandoffClaim,
} from '@/config/auth/loginHandoff';
import { openAuthDialogAndWaitForMessage } from '@/config/auth/officeDialog';

/**
 * Result of the login dialog.
 * - `bootstrap`: a Clerk session JWT obtained via the login surface — the
 *   caller exchanges it for a durable device token (`exchangeBootstrap`).
 * - `device`: the login came home through the handoff claim; a device session
 *   is already stored and there is nothing left to exchange.
 * - `denied`: the login completed but org access was refused — route to the
 *   page explaining the reason, exactly as the fast path's 403 does.
 * - `seat_taken`: the login completed but Outlook was signed in on another
 *   device meanwhile — say so, exactly as the fast path's 409 does.
 * - `cancelled`: the user closed the dialog/popup without signing in — do
 *   nothing.
 */
export type AuthDialogResult =
  | { kind: 'bootstrap'; token: string }
  | { kind: 'device' }
  | { kind: 'denied'; reason: AccessDenialReason | null }
  | { kind: 'seat_taken' }
  | { kind: 'cancelled' };

/** A definitive claim maps 1:1 onto a dialog result; `none` has no mapping. */
function resultFromClaim(claim: Exclude<LoginHandoffClaim, { kind: 'none' }>): AuthDialogResult {
  return claim.kind === 'won' ? { kind: 'device' } : claim;
}

/**
 * Which surface runs the login:
 *
 *  - Office web hosts (Outlook on the web AND new Outlook on Windows, which
 *    reports the same platform): the taskpane is a cross-origin iframe, so a
 *    popup's postMessage can never reach it — the Office Dialog API is the
 *    only channel that does.
 *  - Desktop Office hosts in production: also the Office Dialog API.
 *    `window.open` is explicitly unsupported for add-in auth (blocked in
 *    iframes, popup-blocked, "unpredictable" in desktop webviews per the
 *    Office docs), and its opener/postMessage relay is exactly what a COOP
 *    header severs.
 *  - Desktop in dev builds keeps the popup: the local auth app is plain
 *    http://localhost and dialogs only navigate HTTPS.
 *  - Non-Office contexts (taskpane opened directly in a browser) have no
 *    dialog API at all.
 */
const shouldUseOfficeDialog = (): boolean => {
  if (
    typeof Office === 'undefined' ||
    typeof Office?.context?.ui?.displayDialogAsync !== 'function'
  ) {
    return false;
  }
  if (Office.context.platform === Office.PlatformType.OfficeOnline) {
    return true;
  }
  return !import.meta.env.DEV;
};

/** Office dialog path: messageParent fast path, handoff claim as insurance. */
async function loginViaDialog(handoffId: string): Promise<AuthDialogResult> {
  try {
    const result = await loginViaOfficeDialog(handoffId);
    if (result.type === 'bootstrap') {
      return { kind: 'bootstrap', token: result.token };
    }
    return { kind: 'cancelled' };
  } catch (error) {
    if (error instanceof LoginDialogLostError) {
      // The host lost sight of the window — which is what a COOP header in the
      // sign-in chain looks like, not proof the user closed anything. Ask the
      // backend whether the login completed before concluding cancellation.
      const claim = await claimLoginHandoff(handoffId);
      return claim.kind === 'none' ? { kind: 'cancelled' } : resultFromClaim(claim);
    }
    if (error instanceof LoginDialogTimedOutError) {
      // The dialog stores the handoff before it ever attempts the relay, so a
      // login that completed but never came home (a mangled relay the taskpane
      // ignored, a relay landing right at the deadline) is already claimable.
      // One sweep — a timeout is a weak signal, not worth another minute.
      const claim = await claimLoginHandoffOnce(handoffId);
      if (claim.kind !== 'none') {
        return resultFromClaim(claim);
      }
    }
    throw error;
  }
}

/**
 * Dev/non-Office popup path: `window.opener.postMessage` relay with the
 * shared-localStorage handoff as a local fallback, and the backend handoff
 * claim as the last resort — a COOP hop severs the opener here exactly as it
 * severs the dialog handle.
 */
async function loginViaPopup(handoffId: string): Promise<AuthDialogResult> {
  const url = new URL('/taskpane.html', window.location.origin);
  url.searchParams.set('clerkAuth', '1');
  url.hash = new URLSearchParams({ handoffId }).toString();

  const result = await openAuthDialogAndWaitForMessage(url.toString(), { height: 70, width: 40 });

  if (result.type === 'message') {
    if (!isDialogToParentMessage(result.data)) {
      throw new Error('Unexpected login response');
    }
    const message = result.data;
    if (message.type === 'auth:bootstrap') {
      return { kind: 'bootstrap', token: message.token };
    }
    if (message.type === 'auth:error') {
      throw new Error(message.error);
    }
    // auth:cancel / auth:success without a token — the user did not sign in.
    return { kind: 'cancelled' };
  }

  // The popup closed without a message — or a COOP header made it LOOK closed
  // while the user signs on. Try the shared-jar handoff (desktop webviews share
  // the origin's localStorage), then the backend claim.
  const token = readAndClearBootstrapHandoff();
  if (token) {
    return { kind: 'bootstrap', token };
  }
  const claim = await claimLoginHandoff(handoffId);
  return claim.kind === 'none' ? { kind: 'cancelled' } : resultFromClaim(claim);
}

export const useAuthDialog = () => {
  return useMutation<AuthDialogResult>({
    mutationFn: async () => {
      // The rendezvous secret for this attempt: the dialog parks the login
      // under it at the backend, so the result survives a severed window
      // handle. One id per attempt — never reused.
      const handoffId = generateLoginHandoffId();

      return shouldUseOfficeDialog() ? loginViaDialog(handoffId) : loginViaPopup(handoffId);
    },
    onSuccess: (result) => {
      addBreadcrumb({
        data: { kind: result.kind },
        message: '[useAuthDialog] auth dialog completed',
      });
    },
    retry: false,
  });
};
